import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Building, Loader2, Users, CheckCircle, Crown, Sparkles, Mail, Wand2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import SectionInfoBanner from "@/components/SectionInfoBanner";
import PricingV2Section from "@/components/PricingV2Section";
import BillingInfoCard from "@/components/BillingInfoCard";
import { isIOSApp } from "@/utils/platform";

const API = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_PLANS = {
  base: { name: "Base", max_employees: 5, price: 2.90, activation_fee: 9.90 },
  standard: { name: "Standard", max_employees: 15, price: 4.90, activation_fee: 19.90 },
  premium: { name: "Premium", max_employees: 30, price: 6.90, activation_fee: 29.90 },
  enterprise: { name: "Enterprise", max_employees: 999999, price: 12.90, activation_fee: 39.90 }
};

// Plans info — features per plan (used in the informational "Piani di Abbonamento" section)
const PLAN_DETAILS = [
  {
    key: "base",
    name: "Base",
    price: "€9,90 attivazione",
    employees: "Funzionalità essenziali",
    features: [
      "Approva ferie e permessi",
      "Calendario condiviso",
      "Inserimento malattia e straordinario",
      "Inserimento ferie aziendali",
      "Invio report mensile al consulente per la stesura delle buste paga"
    ]
  },
  {
    key: "standard",
    name: "Standard",
    price: "€19,90 attivazione",
    employees: "Tutto del piano Base più…",
    features: [
      "Personalizzazione App con logo aziendale",
      "Personalizzazione aspetto dell'app",
      "Imposta un giorno di chiusura parziale durante la settimana",
      "Aggiungi festività locali specifiche per la tua zona (es. Santo Patrono)"
    ]
  },
  {
    key: "premium",
    name: "Premium",
    price: "€29,90 attivazione",
    employees: "Tutto del piano Standard più…",
    features: [
      "Notifiche via email oltre alla campanella nell'app",
      "Inserisci date bloccate in cui i dipendenti non potranno inviare richieste",
      "Configura fino a quando i dipendenti possono richiedere ferie e permessi",
      "Supporto per e-mail"
    ],
    highlighted: true
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "€39,90 attivazione",
    employees: "Tutto del piano Premium più…",
    features: [
      "Calendario privato per dipendente: attivabile/disattivabile su singolo dipendente — vedrà solo le proprie ferie/permessi",
      "Aggiungi dipendenti come Amministrativo per assegnargli mansioni varie",
      "Calcola e visualizza nella tua home le ferie residue dei dipendenti e scegli se mostrarle anche nella loro home",
      "Mostra il prospetto ferie dei dipendenti nella tua Home",
      "Supporto per e-mail"
    ]
  }
];

const MONTHLY_TIERS = [
  { range: "Fino a 5 dipendenti", price: "€2,90 / mese" },
  { range: "Da 6 a 15 dipendenti", price: "€4,90 / mese" },
  { range: "Da 16 a 30 dipendenti", price: "€6,90 / mese" },
  { range: "Da 31 a 99 dipendenti", price: "€9,90 / mese" },
  { range: "Oltre 100 dipendenti", price: "€12,90 / mese" }
];

// Format a number to italian locale: 29.9 -> "29,90"
const formatPriceIT = (n) => Number(n).toFixed(2).replace('.', ',');

// Italian ordinal helpers (simple) — falls back to "N°" for numbers we don't have a word for
const ITALIAN_ORDINALS = {
  6: "sesto", 7: "settimo", 8: "ottavo", 9: "nono", 10: "decimo",
  11: "undicesimo", 12: "dodicesimo", 13: "tredicesimo", 14: "quattordicesimo", 15: "quindicesimo",
  16: "sedicesimo", 17: "diciassettesimo", 18: "diciottesimo", 19: "diciannovesimo", 20: "ventesimo",
  21: "ventunesimo", 22: "ventiduesimo", 25: "venticinquesimo",
  30: "trentesimo", 31: "trentunesimo", 32: "trentaduesimo"
};
const italianOrdinal = (n) => ITALIAN_ORDINALS[n] || `${n}°`;

// Given the number of employees, return the next monthly tier price as raw number.
// Returns null if there is no next tier (already in the highest tier).
const getCurrentTierPrice = (n) => {
  if (n <= 5) return 2.90;
  if (n <= 15) return 4.90;
  if (n <= 30) return 6.90;
  if (n <= 99) return 9.90;
  return 12.90;
};
const getPreviousTierPrice = (n) => {
  if (n <= 5) return null; // first tier, no previous
  if (n <= 15) return 2.90;
  if (n <= 30) return 4.90;
  if (n <= 99) return 6.90;
  return 9.90;
};
// Plan max for the previous tier (6 -> 5, 16 -> 15, 31 -> 30, 100 -> 99)
const getPreviousTierMax = (n) => {
  if (n <= 5) return null;
  if (n <= 15) return 5;
  if (n <= 30) return 15;
  if (n <= 99) return 30;
  return 99;
};

export default function AbbonamentoPage() {
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [requestingBonifico, setRequestingBonifico] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [showPayPal, setShowPayPal] = useState(null);
  // Payment method picker modal: "base" | "standard" | "premium" | "enterprise" | null
  const [paymentMethodPlan, setPaymentMethodPlan] = useState(null);
  // "Quale piano fa per me?" wizard state
  const [wizardLogo, setWizardLogo] = useState(null); // true | false | null
  const [wizardEmail, setWizardEmail] = useState(null); // true | false | null
  const [wizardAdmin, setWizardAdmin] = useState(null); // true | false | null
  const [wizardEmployees, setWizardEmployees] = useState("");

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching subscription status:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchPayPalConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/paypal/client-id`);
        setPaypalClientId(response.data.client_id);
      } catch (error) {
        console.log("PayPal not configured");
      }
    };
    
    fetchStatus();
    fetchPayPalConfig();
  }, [token]);

  const handleStripeUpgrade = async (planKey) => {
    setProcessingPayment(true);
    setSelectedPlan(planKey);
    try {
      const response = await axios.post(
        `${API}/api/stripe/create-checkout`,
        {
          plan_key: planKey,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Errore nella creazione del pagamento. Riprova più tardi.");
    } finally {
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  const handleRequestBonifico = async (planKey) => {
    setRequestingBonifico(true);
    setSelectedPlan(planKey);
    try {
      await axios.post(
        `${API}/api/subscription/request-bonifico`,
        { plan_key: planKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Richiesta inviata! Riceverai le istruzioni per il bonifico via email.");
    } catch (error) {
      console.error("Error requesting bonifico:", error);
      toast.error("Errore nell'invio della richiesta. Riprova più tardi.");
    } finally {
      setRequestingBonifico(false);
      setSelectedPlan(null);
    }
  };

  const handleDowngrade = async (planKey) => {
    const targetPlan = SUBSCRIPTION_PLANS[planKey];
    const currentPlan = SUBSCRIPTION_PLANS[status?.piano];
    if (!targetPlan || !currentPlan) return;
    const confirmMsg =
      `Vuoi davvero passare al piano ${targetPlan.name}?\n\n` +
      `• Nessun pagamento sarà richiesto\n` +
      `• Non sono previsti rimborsi sulla differenza di attivazione\n` +
      `• Le funzionalità del piano superiore non saranno più disponibili\n` +
      `• Eventuali futuri upgrade saranno calcolati a partire dal nuovo piano ${targetPlan.name}`;
    if (!window.confirm(confirmMsg)) return;
    setSelectedPlan(planKey);
    try {
      await axios.post(
        `${API}/api/subscription/downgrade`,
        { plan_key: planKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Downgrade al piano ${targetPlan.name} completato!`);
      // Reload status from server (settings.piano + payment_completed must refresh)
      window.location.reload();
    } catch (error) {
      console.error("Error downgrading:", error);
      toast.error(error.response?.data?.detail || "Errore nel downgrade. Riprova più tardi.");
    } finally {
      setSelectedPlan(null);
    }
  };

  const createPayPalOrder = async (planKey) => {
    try {
      const response = await axios.post(
        `${API}/api/paypal/create-order`,
        { plan_key: planKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.order_id;
    } catch (error) {
      console.error("Error creating PayPal order:", error);
      throw error;
    }
  };

  const capturePayPalOrder = async (orderId) => {
    try {
      await axios.post(
        `${API}/api/paypal/capture-order/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Pagamento PayPal completato!");
      window.location.reload();
    } catch (error) {
      console.error("Error capturing PayPal order:", error);
      toast.error("Errore nel completamento del pagamento.");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!status) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Errore nel caricamento dei dati dell'abbonamento.</p>
        </div>
      </Layout>
    );
  }

  const currentPlan = SUBSCRIPTION_PLANS[status.piano] || SUBSCRIPTION_PLANS.base;

  // Nuovo modello di prezzo V2 (Free 0-5 / Pro 6-65 / Enterprise 65+) — solo nuove registrazioni
  if (status.pricing_model === "v2" && !isIOSApp()) {
    return (
      <Layout>
        <PricingV2Section status={status} onRefresh={fetchStatus} />
        {status.payment_completed && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-6">
            <BillingInfoCard />
          </div>
        )}
      </Layout>
    );
  }

  // On iOS: render a strictly informational "Multi-platform service" notice.
  // No subscription plans, no pricing, no payment processors, no external
  // website links — to comply with Apple Guideline 3.1.1 / 3.1.3(b).
  if (isIOSApp()) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h1 className="text-xl font-semibold text-slate-900 mb-2">
              Il tuo abbonamento
            </h1>
            <p className="text-sm text-slate-600 mb-6">
              Il servizio Ferie e Permessi è gestito tramite l'azienda di
              appartenenza. Da questa app puoi consultare lo stato corrente
              del tuo accesso aziendale.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Piano attivo</span>
                <span className="font-semibold text-slate-900" data-testid="ios-plan-name">
                  {currentPlan?.name || "—"}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Stato</span>
                <span className="font-semibold text-emerald-700" data-testid="ios-plan-status">
                  {status.payment_completed
                    ? "Attivo"
                    : status.is_trial
                    ? "In prova gratuita"
                    : "Non attivo"}
                </span>
              </div>
              {status.current_employees !== undefined && (
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Dipendenti registrati</span>
                  <span className="font-semibold text-slate-900">
                    {status.current_employees}
                  </span>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-6 leading-relaxed">
              Per qualsiasi richiesta relativa all'abbonamento aziendale,
              rivolgiti al Titolare della tua azienda.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  // Wizard logic — recommend a plan based on the 3 quick answers.
  // The plans are cumulative (each tier includes the previous), so the highest "Sì" wins.
  // - Admin "Sì"  -> Enterprise
  // - Email "Sì"  -> Premium
  // - Logo  "Sì"  -> Standard
  // - all "No"    -> Base
  const wizardAnyAnswered = wizardLogo !== null || wizardEmail !== null || wizardAdmin !== null;
  let recommendedPlanKey = null;
  if (wizardAnyAnswered) {
    if (wizardAdmin === true) recommendedPlanKey = "enterprise";
    else if (wizardEmail === true) recommendedPlanKey = "premium";
    else if (wizardLogo === true) recommendedPlanKey = "standard";
    else recommendedPlanKey = "base";
  }
  const recommendedPlan = recommendedPlanKey ? SUBSCRIPTION_PLANS[recommendedPlanKey] : null;
  const wizardEmpNum = parseInt(wizardEmployees, 10) || 0;
  const wizardMonthlyPrice = wizardEmpNum > 0 ? getCurrentTierPrice(wizardEmpNum) : null;
  // Determine which monthly tier card should be highlighted (0..3)
  const recommendedTierIndex = wizardEmpNum > 0
    ? (wizardEmpNum <= 5 ? 0 : wizardEmpNum <= 15 ? 1 : wizardEmpNum <= 30 ? 2 : 3)
    : null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <SectionInfoBanner
          sectionKey="abbonamento"
          title="Cosa puoi fare in Abbonamento"
          items={[
            "Vedi il piano attuale e il numero di dipendenti incluso",
            "Confronta i piani disponibili: Base, Standard, Premium, Enterprise",
            "Effettua il pagamento con Carta di Credito (Stripe), PayPal o Bonifico Bancario",
            "Cambia piano in qualsiasi momento — paghi solo la differenza",
            "Piano Free gratuito fino a 5 dipendenti per le nuove aziende",
          ]}
        />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Abbonamento</h1>
          <p className="text-slate-500 mt-1">Gestisci il tuo piano e i metodi di pagamento</p>
        </div>

      {/* Trial Status Banner */}
      {status.is_trial && !status.payment_completed && (
        <Card className={status.trial_expired ? "border-red-300 bg-red-50" : "border-blue-300 bg-blue-50"}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${status.trial_expired ? 'bg-red-100' : 'bg-blue-100'}`}>
                <Sparkles className={`h-6 w-6 ${status.trial_expired ? 'text-red-600' : 'text-blue-600'}`} />
              </div>
              <div>
                {status.trial_expired ? (
                  <>
                    <p className="font-semibold text-red-800">⛔ Prova gratuita terminata</p>
                    <p className="text-sm text-red-600">Effettua il pagamento per continuare ad utilizzare tutte le funzionalità</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-blue-800">🎉 Prova gratuita attiva</p>
                    <p className="text-sm text-blue-600">{status.trial_days_remaining} giorni rimanenti - Scegli un piano per continuare dopo la prova</p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            Piano Attuale
          </CardTitle>
          <CardDescription>Il tuo piano di abbonamento corrente</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
            {/* Plan name - centered and large */}
            <div className="text-center mb-4">
              <p className="text-3xl font-bold text-slate-900" data-testid="current-plan-name">{currentPlan.name}</p>
            </div>

            {/* Activation fee row */}
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-theme-600 shrink-0" />
                <span className="text-base text-slate-700">Importo attivazione</span>
              </div>
              <span className="text-xl font-bold text-theme-600" data-testid="current-plan-activation-fee">
                €{formatPriceIT(currentPlan.activation_fee)}
              </span>
            </div>

            {/* Monthly fee row (based on number of employees) */}
            <div className="flex items-center justify-between py-2 mt-1">
              <div className="flex items-center gap-3 flex-wrap">
                <Users className="h-5 w-5 text-theme-600 shrink-0" />
                <span className="text-base text-slate-700">
                  {status.current_employees} dipendenti
                </span>
                <span className="text-sm text-slate-500">(al mese)</span>
              </div>
              <span className="text-xl font-bold text-theme-600" data-testid="current-plan-monthly-fee">
                €{formatPriceIT(status.tier_price)}
              </span>
            </div>
          </div>
          
          {(() => {
            const n = status.current_employees;
            // Use backend-computed values (tier_price, tier_previous_price, tier_previous_max)
            // so that the banner always reflects the backend's pricing logic (single source of truth).
            const prevPrice = status.tier_previous_price;
            const newPrice = status.tier_price;
            const prevMax = status.tier_previous_max;
            // Tier change message — only when there is a previous tier (i.e. we crossed a threshold)
            if (prevPrice !== null && prevPrice !== undefined && newPrice > prevPrice) {
              const isThresholdHit = prevMax !== null && prevMax !== undefined && n === prevMax + 1;
              return (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 text-amber-800 border border-amber-200">
                  {isThresholdHit ? (
                    <>
                      Hai registrato il <strong>{italianOrdinal(n)}</strong> dipendente, quindi la prossima scadenza sarà <strong>€{formatPriceIT(newPrice)}</strong>.
                    </>
                  ) : (
                    <>
                      Hai superato il limite di <strong>{prevMax} dipendenti</strong>, quindi la prossima scadenza sarà <strong>€{formatPriceIT(newPrice)}</strong>.
                    </>
                  )}
                </div>
              );
            }
            return null;
          })()}
        </CardContent>
      </Card>

      {/* "Quale piano fa per me?" — Quick recommendation wizard (only during trial, hidden after first purchase) */}
      {!status.payment_completed && (
      <Card data-testid="plan-wizard" className="border-2 border-theme-200 bg-gradient-to-br from-theme-50 to-slate-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wand2 className="h-5 w-5 text-theme-600" />
            Quale piano fa per me?
          </CardTitle>
          <CardDescription>Rispondi a 4 domande rapide e ti suggeriamo il piano più adatto con il prospetto completo dei costi</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
            {/* Q1 — logo */}
            <div className="flex flex-col h-full">
              <label className="flex-1 text-xs font-semibold text-slate-700 block mb-2">
                1. Vuoi personalizzare con il logo aziendale?
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setWizardLogo(true)}
                  className={`px-3 py-2 rounded-md text-sm font-medium border-2 transition-all ${wizardLogo === true ? 'bg-theme-500 text-white border-theme-500' : 'bg-white text-slate-700 border-slate-300 hover:border-theme-400'}`}
                  data-testid="wizard-logo-yes"
                >
                  Sì
                </button>
                <button
                  type="button"
                  onClick={() => setWizardLogo(false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium border-2 transition-all ${wizardLogo === false ? 'bg-theme-500 text-white border-theme-500' : 'bg-white text-slate-700 border-slate-300 hover:border-theme-400'}`}
                  data-testid="wizard-logo-no"
                >
                  No
                </button>
              </div>
            </div>

            {/* Q2 — email notifications */}
            <div className="flex flex-col h-full">
              <label className="flex-1 text-xs font-semibold text-slate-700 block mb-2">
                2. Vuoi ricevere notifiche per e-mail?
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setWizardEmail(true)}
                  className={`px-3 py-2 rounded-md text-sm font-medium border-2 transition-all ${wizardEmail === true ? 'bg-theme-500 text-white border-theme-500' : 'bg-white text-slate-700 border-slate-300 hover:border-theme-400'}`}
                  data-testid="wizard-email-yes"
                >
                  Sì
                </button>
                <button
                  type="button"
                  onClick={() => setWizardEmail(false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium border-2 transition-all ${wizardEmail === false ? 'bg-theme-500 text-white border-theme-500' : 'bg-white text-slate-700 border-slate-300 hover:border-theme-400'}`}
                  data-testid="wizard-email-no"
                >
                  No
                </button>
              </div>
            </div>

            {/* Q3 — admin roles */}
            <div className="flex flex-col h-full">
              <label className="flex-1 text-xs font-semibold text-slate-700 block mb-2">
                3. Vuoi aggiungere dipendenti come Amministrativo per assegnargli mansioni che espleteranno per conto tuo?
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setWizardAdmin(true)}
                  className={`px-3 py-2 rounded-md text-sm font-medium border-2 transition-all ${wizardAdmin === true ? 'bg-theme-500 text-white border-theme-500' : 'bg-white text-slate-700 border-slate-300 hover:border-theme-400'}`}
                  data-testid="wizard-admin-yes"
                >
                  Sì
                </button>
                <button
                  type="button"
                  onClick={() => setWizardAdmin(false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium border-2 transition-all ${wizardAdmin === false ? 'bg-theme-500 text-white border-theme-500' : 'bg-white text-slate-700 border-slate-300 hover:border-theme-400'}`}
                  data-testid="wizard-admin-no"
                >
                  No
                </button>
              </div>
            </div>

            {/* Q4 — number of employees (for full price prospectus) */}
            <div className="flex flex-col h-full">
              <label className="flex-1 text-xs font-semibold text-slate-700 block mb-2">
                4. Quanti dipendenti hai?
              </label>
              <input
                type="number"
                min="1"
                placeholder="Es. 8"
                value={wizardEmployees}
                onChange={(e) => setWizardEmployees(e.target.value.replace(/[^0-9]/g, ""))}
                className="w-full px-3 py-2 rounded-md border-2 border-slate-300 bg-white text-sm focus:outline-none focus:border-theme-400 focus:ring-2 focus:ring-theme-200"
                data-testid="wizard-employees-input"
              />
            </div>
          </div>

          {/* Result */}
          {recommendedPlan && (
            <div
              className="p-4 bg-white rounded-lg border-2 border-theme-500 shadow-sm"
              data-testid="wizard-result"
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--theme-100)' }}>
                  <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-600)' }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-600">Per le tue esigenze ti consigliamo:</p>
                  <p className="text-xl font-bold text-slate-900 mt-0.5">
                    Piano {recommendedPlan.name}
                  </p>
                  {wizardMonthlyPrice ? (
                    <div className="mt-3 space-y-2">
                      <div className="p-3 rounded-md bg-theme-50 border border-theme-200">
                        <p className="text-xs font-semibold uppercase tracking-wider text-theme-700">Al primo acquisto pagherai solo:</p>
                        <p className="text-sm text-slate-800 mt-1">
                          Piano {recommendedPlan.name} (attivazione una tantum):{" "}
                          <strong className="text-theme-700 text-base">€{formatPriceIT(recommendedPlan.activation_fee)}</strong>
                        </p>
                      </div>
                      <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                        <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Dal mese successivo (rinnovo automatico):</p>
                        <p className="text-sm text-slate-800 mt-1">
                          Tariffa mensile per {wizardEmpNum} {wizardEmpNum === 1 ? 'dipendente' : 'dipendenti'}:{" "}
                          <strong className="text-amber-800 text-base">€{formatPriceIT(wizardMonthlyPrice)}/mese</strong>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-700 mt-1">
                      <strong>€{formatPriceIT(recommendedPlan.activation_fee)}</strong> di attivazione una tantum + tariffa mensile in base ai dipendenti registrati
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-2">
                    Scorri qui sotto per vedere tutti i dettagli del piano consigliato (evidenziato).
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Plans overview — informational catalog of all 4 plans + monthly tiers */}
      <Card data-testid="plans-overview">
        <CardHeader className="text-center pb-2">
          <div className="inline-flex items-center justify-center p-3 rounded-full mb-2 mx-auto" style={{ backgroundColor: 'var(--theme-100)' }}>
            <CreditCard className="h-6 w-6" style={{ color: 'var(--theme-600)' }} />
          </div>
          <CardTitle className="text-2xl">Piani di Acquisto</CardTitle>
          <CardDescription className="max-w-xl mx-auto">
            Il <strong>piano</strong> determina le funzioni dell'app (lo paghi una volta sola alla scelta).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Upgrade reassurance banner — visible to base/standard/premium users (not enterprise) */}
          {["base", "standard", "premium"].includes(status.piano) && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="upgrade-anytime-banner">
              <Sparkles className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 leading-relaxed">
                <strong>Puoi fare l'upgrade al piano superiore in qualsiasi momento</strong>, pagando solo la differenza tra i due piani. Nessun costo aggiuntivo, nessun vincolo.
              </p>
            </div>
          )}

          {/* 1) Plans cards */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 text-center">
              1. Scegli il piano (in base alle funzioni che ti servono)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {PLAN_DETAILS.map((plan, index) => {
                const isCurrentPlan = status.payment_completed && status.piano === plan.key;
                // During the free trial, the company is on Base but hasn't paid yet.
                // We still want to show "Piano Attuale (in prova)" on the Base card
                // so the user understands where they are starting from.
                const isTrialPlan = !status.payment_completed && status.piano === plan.key;
                const isRecommended = recommendedPlanKey === plan.key && !isCurrentPlan && !isTrialPlan;
                // Compute pricing display info — same logic as the old "Scegli il tuo Piano" card
                let priceDisplay = plan.price;
                let priceSubLabel = null;
                let isDowngrade = false;
                if (status.payment_completed && status.piano && SUBSCRIPTION_PLANS[status.piano] && SUBSCRIPTION_PLANS[plan.key]) {
                  if (isCurrentPlan) {
                    priceDisplay = "Piano attuale";
                  } else {
                    const oldPlan = SUBSCRIPTION_PLANS[status.piano];
                    const newPlan = SUBSCRIPTION_PLANS[plan.key];
                    const delta = newPlan.activation_fee - oldPlan.activation_fee;
                    if (delta > 0) {
                      priceDisplay = `+ €${delta.toFixed(2).replace('.', ',')}`;
                      priceSubLabel = "Con l'upgrade paghi solo la differenza";
                    } else if (delta < 0) {
                      isDowngrade = true;
                      priceDisplay = "Downgrade";
                      priceSubLabel = "Nessun rimborso";
                    } else {
                      priceDisplay = "€0,00";
                      priceSubLabel = "Stesso prezzo";
                    }
                  }
                }
                return (
                  <Card
                    key={index}
                    onClick={async () => {
                      if (isCurrentPlan) return;
                      // Refresh status so we have fresh payment_completed/piano before
                      // deciding between downgrade (instant) vs upgrade (open payment modal)
                      const fresh = await fetchStatus();
                      const effStatus = fresh || status;
                      const effPiano = effStatus?.piano;
                      const effPaid = effStatus?.payment_completed;
                      // Downgrade (only after first purchase, target plan cheaper than current): apply directly, no payment modal
                      const targetPlanData = SUBSCRIPTION_PLANS[plan.key];
                      const currentPlanData = SUBSCRIPTION_PLANS[effPiano];
                      if (
                        effPaid &&
                        currentPlanData &&
                        targetPlanData &&
                        plan.key !== effPiano &&
                        targetPlanData.activation_fee < currentPlanData.activation_fee
                      ) {
                        handleDowngrade(plan.key);
                        return;
                      }
                      if (effPaid && plan.key === effPiano) {
                        // Same plan already active — nothing to do
                        return;
                      }
                      // First purchase or upgrade: open payment method picker
                      setPaymentMethodPlan(plan.key);
                    }}
                    className={`border-2 transition-all relative ${
                      isCurrentPlan ? 'border-theme-400 bg-theme-50 cursor-default' : 'cursor-pointer hover:shadow-lg hover:border-theme-400'
                    } ${
                      isTrialPlan ? 'border-amber-400 bg-amber-50/50' : ''
                    } ${
                      isRecommended ? 'ring-2 ring-amber-400 ring-offset-2 border-amber-400 shadow-lg' : ''
                    } ${
                      plan.highlighted && !isCurrentPlan && !isTrialPlan && !isRecommended ? 'border-theme-500 shadow-md' : ''
                    } ${
                      !isCurrentPlan && !isTrialPlan && !plan.highlighted && !isRecommended ? 'border-slate-200' : ''
                    }`}
                    data-testid={`plan-card-${plan.key}`}
                  >
                    {isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-theme-500 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                          Piano Attuale
                        </span>
                      </div>
                    )}
                    {isTrialPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                          Piano Attuale (in prova)
                        </span>
                      </div>
                    )}
                    {isRecommended && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-amber-400 text-amber-950 text-xs font-bold px-3 py-1 rounded-full shadow flex items-center gap-1 whitespace-nowrap">
                          <Sparkles className="h-3 w-3" />
                          Consigliato per te
                        </span>
                      </div>
                    )}
                    {plan.highlighted && !isCurrentPlan && !isTrialPlan && !isRecommended && (
                      <div className="text-center py-1 text-xs font-medium text-white rounded-t-lg" style={{ backgroundColor: 'var(--theme-500)' }}>
                        Più popolare
                      </div>
                    )}
                    <CardHeader className="text-center pb-2 px-3">
                      <CardTitle className="text-lg">{plan.name}</CardTitle>
                      <div className="mt-1">
                        <span className={`text-xl font-bold ${isDowngrade ? 'text-amber-600' : 'text-slate-900'}`}>
                          {priceDisplay}
                        </span>
                      </div>
                      {priceSubLabel && (
                        <p className="text-[11px] text-slate-500 mt-0.5">{priceSubLabel}</p>
                      )}
                      <CardDescription className="text-sm font-medium" style={{ color: 'var(--theme-600)' }}>
                        {plan.employees}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="px-3 pt-0">
                      <ul className="space-y-1.5">
                        {plan.features.map((feature, fi) => (
                          <li key={fi} className="flex items-start gap-1.5 text-xs text-slate-600">
                            <CheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: 'var(--theme-500)' }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {!isCurrentPlan && (
                        <p className="text-[11px] text-center text-theme-600 font-semibold mt-3 pt-2 border-t border-slate-100">
                          Clicca per acquistare →
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* 2) Monthly tiers */}
          <div className="pt-4 border-t border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900 text-center">Piani mensili</h2>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 text-center mt-2">
              2. Tariffa mensile automatica in base ai dipendenti registrati
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {MONTHLY_TIERS.map((tier, idx) => {
                const isRecommendedTier = recommendedTierIndex === idx;
                return (
                  <Card
                    key={idx}
                    className={`border-2 transition-all cursor-default hover:shadow-lg hover:border-theme-400 relative ${
                      isRecommendedTier ? 'ring-2 ring-amber-400 ring-offset-2 border-amber-400 shadow-lg' : 'border-slate-200'
                    }`}
                    data-testid={`monthly-tier-${idx}`}
                  >
                    {isRecommendedTier && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-amber-400 text-amber-950 text-xs font-bold px-3 py-1 rounded-full shadow flex items-center gap-1 whitespace-nowrap">
                          <Sparkles className="h-3 w-3" />
                          La tua fascia
                        </span>
                      </div>
                    )}
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1">{tier.range}</p>
                      <p className="text-lg font-bold" style={{ color: 'var(--theme-600)' }}>{tier.price}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 text-center mt-3">
              La tariffa mensile è indipendente dal piano scelto: passa di fascia automaticamente quando aggiungi o elimini dipendenti.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <div className="p-4 bg-slate-100 rounded-lg text-sm text-slate-600">
        <p className="font-medium mb-2">ℹ️ Informazioni sui pagamenti</p>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li>I pagamenti con carta sono processati in modo sicuro tramite Stripe</li>
          <li>PayPal permette di pagare con il tuo account o carta di credito</li>
          <li>Per il bonifico bancario riceverai le istruzioni via email</li>
          <li>L'abbonamento si attiva immediatamente dopo il pagamento con carta di credito o PayPal, mediante bonifico entro 24/48 ore dopo l'invio della contabile bancaria</li>
        </ul>
      </div>

      </div>

      {/* Payment method picker modal — shown when user clicks a plan card */}
      {paymentMethodPlan && (() => {
        const plan = SUBSCRIPTION_PLANS[paymentMethodPlan];
        const isUpgradeOrDowngrade = status.payment_completed && status.piano && SUBSCRIPTION_PLANS[status.piano];
        let amountText, isDowngrade = false;
        if (isUpgradeOrDowngrade) {
          const oldPlan = SUBSCRIPTION_PLANS[status.piano];
          const delta = plan.activation_fee - oldPlan.activation_fee;
          if (delta > 0) {
            amountText = `+ €${delta.toFixed(2).replace('.', ',')} (differenza upgrade)`;
          } else if (delta < 0) {
            isDowngrade = true;
            amountText = "Downgrade — nessun rimborso";
          } else {
            amountText = "Stesso prezzo del piano attuale";
          }
        } else {
          amountText = `€${plan.activation_fee.toFixed(2).replace('.', ',')} attivazione`;
        }
        const showPayPalHere = showPayPal === paymentMethodPlan;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setPaymentMethodPlan(null); setShowPayPal(null); }} data-testid="payment-method-modal">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Piano {plan.name}
              </h3>
              <p className={`text-base font-semibold mb-4 ${isDowngrade ? 'text-amber-700' : 'text-theme-700'}`}>
                {amountText}
              </p>
              <p className="text-sm text-slate-600 mb-5">
                {isDowngrade ? "Conferma il downgrade scegliendo come vuoi gestire la transazione:" : "Come vuoi pagare?"}
              </p>

              {showPayPalHere ? (
                <div className="space-y-3">
                  <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "EUR" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", height: 45 }}
                      createOrder={() => createPayPalOrder(paymentMethodPlan)}
                      onApprove={async (data) => {
                        await capturePayPalOrder(data.orderID);
                        setPaymentMethodPlan(null);
                        setShowPayPal(null);
                      }}
                      onCancel={() => setShowPayPal(null)}
                      onError={(err) => {
                        console.error("PayPal error:", err);
                        toast.error("Errore PayPal. Riprova.");
                        setShowPayPal(null);
                      }}
                    />
                  </PayPalScriptProvider>
                  <Button variant="ghost" className="w-full" onClick={() => setShowPayPal(null)} data-testid="paypal-cancel-btn">
                    Indietro
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {!isUpgradeOrDowngrade && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 leading-relaxed" data-testid="first-purchase-notice">
                      <strong>Primo acquisto:</strong> per attivare la tariffa mensile automatica devi pagare con Carta di Credito.
                      <br />Per gli acquisti successivi (upgrade) potrai usare anche PayPal o Bonifico Bancario.
                    </div>
                  )}
                  <Button
                    onClick={() => { handleStripeUpgrade(paymentMethodPlan); setPaymentMethodPlan(null); }}
                    disabled={processingPayment || requestingBonifico}
                    className="w-full btn-theme h-12 gap-2 justify-start text-base"
                    data-testid="pay-stripe-btn"
                  >
                    <CreditCard className="h-5 w-5" />
                    Paga con Carta
                  </Button>
                  {paypalClientId && isUpgradeOrDowngrade && (
                    <Button
                      variant="outline"
                      onClick={() => setShowPayPal(paymentMethodPlan)}
                      disabled={processingPayment || requestingBonifico}
                      className="w-full h-12 gap-2 justify-start text-base"
                      data-testid="pay-paypal-btn"
                    >
                      <span className="text-blue-600 font-bold">Pay</span>
                      <span className="text-blue-400 font-bold">Pal</span>
                    </Button>
                  )}
                  {isUpgradeOrDowngrade && (
                    <Button
                      variant="outline"
                      onClick={() => { handleRequestBonifico(paymentMethodPlan); setPaymentMethodPlan(null); }}
                      disabled={processingPayment || requestingBonifico}
                      className="w-full h-12 gap-2 justify-start text-base"
                      data-testid="pay-bonifico-btn"
                    >
                      {requestingBonifico ? <Loader2 className="h-5 w-5 animate-spin" /> : <Building className="h-5 w-5" />}
                      Bonifico bancario
                    </Button>
                  )}
                  <Button variant="ghost" className="w-full" onClick={() => setPaymentMethodPlan(null)} data-testid="payment-method-cancel-btn">
                    Annulla
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {status.payment_completed && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6">
          <BillingInfoCard />
        </div>
      )}

      {/* (legacy confirmation modal removed — payment method picker now handles everything) */}
    </Layout>
  );
}
